import React, { useCallback, useEffect, useRef, useState } from "react"
import Modal from "react-modal"
import ReactLoading from "react-loading"
import { Plus } from "styled-icons/feather"

import Button from "components/common/Button"
import { Headline3 } from "components/common/styles"
import { modalStyles } from "components/modals/closeComposerModal"
import { theme } from "theme"

import * as styles from "../styles"
import { Picture } from "../types"
import { fetchPictures } from "../picturesApi"
import { usePictureUpload } from "../usePictureUpload"

type PicturePickerModalProps = {
  isOpen: boolean
  accountId: string
  profileId: string
  currentPictureId?: string | null // preselected; "Use picture" stays disabled until a different one is picked
  onSelect: (pictureId: string) => void
  onClose: () => void
}

const PicturePickerModal: React.FC<PicturePickerModalProps> = ({
  isOpen,
  accountId,
  profileId,
  currentPictureId,
  onSelect,
  onClose,
}) => {
  const reactModalStyles = modalStyles(440)
  const [pictures, setPictures] = useState<Picture[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasLoadFailed, setHasLoadFailed] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(currentPictureId ?? null)
  const pickedSinceUploadStartedRef = useRef(false)

  const showUploadedPictures = (createdPictures: Picture[]) => {
    const createdIds = new Set(createdPictures.map((picture) => picture.id))
    // Use picture doesn't wait for the upload, so a reopen's reload may already list these.
    setPictures((previous) => [...createdPictures, ...previous.filter((picture) => !createdIds.has(picture.id))])
    const newestUsablePicture = createdPictures.find((picture) => picture.usableForVisual)
    if (newestUsablePicture && !pickedSinceUploadStartedRef.current) setSelectedId(newestUsablePicture.id)
  }

  const { uploadingCount, statusMessage, isDragging, dropZoneProps, fileInputProps, openFilePicker } = usePictureUpload({
    accountId,
    profileId,
    disabled: isLoading || hasLoadFailed,
    onUploadStarted: () => {
      pickedSinceUploadStartedRef.current = false
    },
    onPicturesAdded: showUploadedPictures,
  })
  const isUploading = uploadingCount > 0
  const canUse = !!selectedId && selectedId !== currentPictureId

  const loadPictures = useCallback(() => {
    setIsLoading(true)
    setHasLoadFailed(false)
    fetchPictures(accountId, profileId)
      .then(setPictures)
      .catch((err: unknown) => {
        console.error("Failed to fetch pictures", { accountId, profileId, err })
        setHasLoadFailed(true)
      })
      .finally(() => setIsLoading(false))
  }, [accountId, profileId])

  useEffect(() => {
    if (!isOpen) return
    setSelectedId(currentPictureId ?? null)
    loadPictures()
  }, [isOpen, currentPictureId, loadPictures])

  const pickPicture = (pictureId: string) => {
    pickedSinceUploadStartedRef.current = true
    setSelectedId(pictureId)
  }

  const handleClose = () => {
    setSelectedId(null)
    onClose()
  }

  const handleUse = () => {
    if (!canUse) return
    onSelect(selectedId)
    setSelectedId(null)
  }

  return (
    <Modal
      // @ts-ignore
      name={"picture-picker-modal"}
      ariaHideApp={false}
      isOpen={isOpen}
      // @ts-ignore
      style={reactModalStyles}
      onRequestClose={handleClose}
    >
      <styles.PicturePickerDropZone
        $isDragging={isDragging}
        {...dropZoneProps}
        data-testid="picture-picker-drop-zone"
      >
        <styles.ModalContentWrapper>
          <Headline3>Choose a picture</Headline3>
          <styles.ModalInfoPara>The visual is rebuilt with the picture you pick.</styles.ModalInfoPara>
        </styles.ModalContentWrapper>

        {isLoading && (
          <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
            <ReactLoading type="spin" color={theme.colors.text02} width={28} height={28} />
          </div>
        )}

        {hasLoadFailed && (
          <styles.PicturePickerLoadFailed role="alert">
            Your pictures couldn't be loaded.
            <Button variant="secondary" onClick={loadPictures}>
              Try again
            </Button>
          </styles.PicturePickerLoadFailed>
        )}

        {!isLoading && !hasLoadFailed && (
          <styles.PicturePickerFilmstrip data-testid="picture-picker-filmstrip">
            <styles.PicturePickerAddTile
              type="button"
              onClick={openFilePicker}
              disabled={isUploading}
              title="Add pictures"
              data-testid="picture-picker-add"
            >
              <Plus size={20} />
            </styles.PicturePickerAddTile>
            {Array.from({ length: uploadingCount }, (_, index) => (
              <styles.PicturePickerUploadingThumb key={`uploading-${index}`} data-testid="picture-picker-uploading">
                <ReactLoading type="spin" color={theme.colors.text02} width={24} height={24} />
                <styles.PicturePickerUploadingLabel>{statusMessage}</styles.PicturePickerUploadingLabel>
              </styles.PicturePickerUploadingThumb>
            ))}
            {pictures.length === 0 && !isUploading && (
              <styles.PicturePickerEmpty>Drop pictures here or click to add</styles.PicturePickerEmpty>
            )}
            {pictures.map((picture) => (
              <styles.PicturePickerThumb
                key={picture.id}
                type="button"
                $selected={selectedId === picture.id}
                $disabled={!picture.usableForVisual}
                disabled={!picture.usableForVisual}
                title={picture.usableForVisual ? undefined : picture.unusableReason}
                style={{ backgroundImage: `url(${picture.url})` }}
                onClick={() => pickPicture(picture.id)}
                aria-label={picture.usableForVisual ? "Picture" : "Picture, can't be used as a background"}
                aria-pressed={selectedId === picture.id}
                data-testid={`picture-picker-thumb-${picture.id}`}
              >
                {selectedId === picture.id && <styles.PicturePickerCheck>✓</styles.PicturePickerCheck>}
                {!picture.usableForVisual && <styles.PicturePickerUnusable>Can't be used</styles.PicturePickerUnusable>}
              </styles.PicturePickerThumb>
            ))}
          </styles.PicturePickerFilmstrip>
        )}

        <styles.ModalButtonGroup>
          {/* @ts-ignore */}
          <Button variant="primary" onClick={handleUse} disabled={!canUse} data-testid="picture-picker-use">
            Use picture
          </Button>
          {/* @ts-ignore */}
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
        </styles.ModalButtonGroup>
      </styles.PicturePickerDropZone>
      <input {...fileInputProps} data-testid="picture-picker-file-input" />
    </Modal>
  )
}

export default PicturePickerModal
